import React, { useState } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
    Chip,
    FormControlLabel,
    Grid,
    IconButton,
    Input as MuiInput,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { setSnackBar } from "../../../store/common/commonSlice";
import AutoCompleteServerSide from "../../Common/AutoCompleteServerSide";
import Button from "../../Common/Button";
import { baseUrl } from "../../../helpers/constants";
import {
    buildCandidateImportPayload,
    candidateImportEditableFields,
    normalizeCandidateDraft,
    parseSpreadsheetFile,
} from "../../../helpers/candidateImport";
import {
    useBulkAddNewCandidateMutation,
    useExtractCandidatePdfMutation,
} from "../../../store/candidate/candidateApis";

const emptyImportState = {
    fileName: "",
    sourceType: "",
    rows: [],
};

export const BulkImportCandidates = ({ getSansthaList }) => {
    const dispatch = useDispatch();
    const [bulkAddCandidate, { isLoading: importLoading }] = useBulkAddNewCandidateMutation();
    const [extractCandidatePdf, { isLoading: extractingPdf }] = useExtractCandidatePdfMutation();
    const [importState, setImportState] = useState(emptyImportState);
    const [sansthaId, setSansthaId] = useState(null);
    const [showToEveryone, setShowToEveryone] = useState(true);

    const handleReset = () => {
        const fileInput = document.getElementById("candidate-import-file");
        if (fileInput) {
            fileInput.value = "";
        }

        setImportState(emptyImportState);
        setSansthaId(null);
    };

    const handleRowChange = (index, field, value) => {
        setImportState((prev) => {
            const nextRows = [...prev.rows];
            nextRows[index] = normalizeCandidateDraft({
                ...nextRows[index],
                [field]: value,
            });

            return {
                ...prev,
                rows: nextRows,
            };
        });
    };

    const handleRemoveRow = (index) => {
        setImportState((prev) => ({
            ...prev,
            rows: prev.rows.filter((_, rowIndex) => rowIndex !== index),
        }));
    };

    const handleFileReading = async (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        try {
            const extension = file.name.split(".").pop()?.toLowerCase();
            let rows = [];

            if (extension === "pdf") {
                const formData = new FormData();
                formData.append("image", file);
                const result = await extractCandidatePdf(formData).unwrap();
                rows = (result?.records || []).map((row) => normalizeCandidateDraft(row));
            } else {
                rows = await parseSpreadsheetFile(file);
            }

            if (!rows.length) {
                throw new Error("No candidate records could be prepared from the uploaded file.");
            }

            setImportState({
                fileName: file.name,
                sourceType: extension === "pdf" ? "pdf" : "spreadsheet",
                rows,
            });
        } catch (error) {
            handleReset();
            dispatch(setSnackBar({
                open: true,
                message: error?.data?.message || error.message,
                severity: "error",
            }));
        }
    };

    const handleFileUpload = async () => {
        try {
            if (!sansthaId) {
                throw new Error("Please select a sanstha before importing.");
            }

            if (!importState.rows.length) {
                throw new Error("Please upload a file with candidate records.");
            }

            const payload = buildCandidateImportPayload(importState.rows, sansthaId, showToEveryone);

            await bulkAddCandidate(payload).unwrap();
            dispatch(setSnackBar({
                open: true,
                message: "Data imported successfully.",
                severity: "success",
            }));

            handleReset();
        } catch (error) {
            dispatch(setSnackBar({
                open: true,
                message: error?.data?.message || error.message,
                severity: "error",
            }));
        }
    };

    return (
        <Grid container spacing={2} pt={2}>
            <Grid item xs={12} md={4}>
                <AutoCompleteServerSide
                    label="Type & Select Sanstha *"
                    name="sanstha_id"
                    id="sanstha_id"
                    fullWidth
                    defaultValue={sansthaId}
                    fetchDataFunction={(data) => getSansthaList(data)}
                    onChange={(value) => setSansthaId(value)}
                    disabled={false}
                    isMultiple={false}
                />
            </Grid>
            <Grid item xs={12} md={4}>
                <MuiInput
                    type="file"
                    name="file"
                    id="candidate-import-file"
                    inputProps={{ accept: ".csv,.xlsx,.xls,.pdf" }}
                    onChange={handleFileReading}
                />
                <Typography variant="caption">
                    Upload CSV/XLSX for sheet import or biodata PDF for text extraction.
                </Typography>
                <br />
                <Typography variant="caption">
                    PDF extraction works for text-based biodata files like the sample. Scanned image PDFs still need OCR support.
                </Typography>
                <br />
                <Typography variant="caption">
                    Download sample file <a rel="noreferrer" href={baseUrl + "/public/sample/member-import-sample.csv"} target="_blank">here</a>
                </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
                <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="Show to everyone"
                    onChange={(event) => setShowToEveryone(event.target.checked)}
                />
            </Grid>

            {importState.rows.length > 0 ? (
                <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ xs: "flex-start", md: "center" }}>
                            <Chip label={`${importState.rows.length} record(s) ready`} color="primary" variant="outlined" />
                            <Chip label={importState.fileName} variant="outlined" />
                            <Chip label={importState.sourceType === "pdf" ? "PDF extraction" : "Spreadsheet import"} variant="outlined" />
                        </Stack>
                    </Paper>
                </Grid>
            ) : null}

            {importState.rows.length > 0 ? (
                <Grid item xs={12}>
                    <Stack spacing={2}>
                        {importState.rows.map((row, index) => (
                            <Paper key={`${row._meta?.pageNumber || "row"}-${index}`} variant="outlined" sx={{ p: 2 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Grid container justifyContent="space-between" alignItems="center">
                                            <Grid item>
                                                <Typography variant="subtitle1">
                                                    Record {index + 1}
                                                    {row._meta?.pageNumber ? ` | Page ${row._meta.pageNumber}` : ""}
                                                </Typography>
                                            </Grid>
                                            <Grid item>
                                                <IconButton onClick={() => handleRemoveRow(index)}>
                                                    <DeleteOutlineIcon />
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    {row._meta?.warnings?.length > 0 ? (
                                        <Grid item xs={12}>
                                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                {row._meta.warnings.map((warning) => (
                                                    <Chip key={warning} label={warning} color="warning" size="small" variant="outlined" />
                                                ))}
                                            </Stack>
                                        </Grid>
                                    ) : null}

                                    {candidateImportEditableFields.map((fieldConfig) => (
                                        <Grid item xs={12} md={fieldConfig.multiline ? 12 : 4} key={fieldConfig.field}>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                multiline={Boolean(fieldConfig.multiline)}
                                                minRows={fieldConfig.multiline ? 3 : undefined}
                                                label={fieldConfig.label}
                                                value={row[fieldConfig.field] || ""}
                                                onChange={(event) => handleRowChange(index, fieldConfig.field, event.target.value)}
                                            />
                                        </Grid>
                                    ))}

                                    {row._meta?.rawText ? (
                                        <Grid item xs={12}>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                multiline
                                                minRows={6}
                                                label="Extracted PDF Text"
                                                value={row._meta.rawText}
                                                InputProps={{ readOnly: true }}
                                            />
                                        </Grid>
                                    ) : null}
                                </Grid>
                            </Paper>
                        ))}
                    </Stack>
                </Grid>
            ) : null}

            <Grid item xs={12}>
                <Grid sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Button
                            size="large"
                            type="button"
                            variant="outlined"
                            color="inherit"
                            fullWidth={false}
                            onClick={handleReset}
                            disabled={!importState.rows.length && !importState.fileName}
                        >
                            Reset
                        </Button>
                        <Button
                            size="large"
                            type="submit"
                            loading={importLoading || extractingPdf}
                            fullWidth={false}
                            onClick={handleFileUpload}
                            disabled={!importState.rows.length}
                        >
                            Import Candidates
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};
